// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { EventProvider, GlobalEventSources } from '@imprivata-cloud/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  Outlet,
  type RouteObject,
  RouterProvider,
} from 'react-router-dom';

import { IcpCoreFlow } from './containers/icp-core';
import { OidcFlow } from './containers/oidc';
import { TenantLogin } from './containers/tenant-lookup';
import { Notifications } from './errorHandler/context/Notifications';
import ErrorBoundary from './errorHandler/ErrorBoundary';
import { ErrorView } from './errorHandler/ErrorView';
import { LocaleProvider } from './i18n/localeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const AppShell: React.FC = () => {
  const { t } = useTranslation();
  const translate = useCallback((i18nKey: string) => t(i18nKey), [t]);

  return (
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <EventProvider>
          <GlobalEventSources translate={translate} disableCameraEvents />
          <Outlet />
        </EventProvider>
      </QueryClientProvider>
    </LocaleProvider>
  );
};

const routes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <ErrorView />,
    children: [
      {
        path: 'tenant-lookup/*',
        element: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Notifications key="tenant-lookup" translationRoot="tenant-lookup">
              <TenantLogin />
            </Notifications>
          </ErrorBoundary>
        ),
      },
      {
        path: 'oidc',
        element: (
          <div className="authn-container">
            <OidcFlow />
          </div>
        ),
      },
      {
        path: '*',
        element: (
          <div className="authn-container">
            <IcpCoreFlow />
          </div>
        ),
      },
    ],
  },
];

const router = createBrowserRouter(routes, {
  basename: import.meta.env.BASE_URL,
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
  },
});

export const App: React.FC = () => (
  <RouterProvider router={router} future={{ v7_startTransition: true }} />
);
